import type { AgentEventType } from "./types";

export const SUPPRESSION_WINDOW_MS = 60_000;
export const STALL_THRESHOLD_MS = 35_000;

/**
 * The events the tutor will actually speak for. A wrong answer is the only
 * thing it speaks up for on its own: `activity_completed` and `stall` used to
 * fire too, and a tutor that interrupts a learner who is doing fine reads as
 * pestering. Every other event still lands in `recentEvents`, so the tutor sees
 * the stall or the finished activity - it just doesn't open its mouth about it.
 */
const SPEAKING_EVENTS: readonly AgentEventType[] = [
  "answer_submitted_incorrect",
  "manual_help_requested",
];

const TRIGGER_DELAY_MAP: Partial<Record<AgentEventType, number>> = {
  answer_submitted_incorrect: 2500,
  manual_help_requested: 0,
};

/**
 * Returns true for events that make the tutor say something. Everything else is
 * context only.
 */
export function firesTutorMessage(eventType: AgentEventType): boolean {
  return SPEAKING_EVENTS.includes(eventType);
}

/**
 * Returns true when the tutor would be speaking of its own accord rather than
 * because the learner asked. Unprompted turns are the ones that must not steal
 * the screen, and the ones the awaiting-reply latch holds back.
 */
export function isUnprompted(eventType: AgentEventType): boolean {
  return !bypassesSuppression(eventType);
}

/**
 * Returns true if the agent is currently suppressed (within suppression window).
 */
export function isSuppressed(lastFiredAt: number | null): boolean {
  if (lastFiredAt === null) return false;
  return Date.now() - lastFiredAt < SUPPRESSION_WINDOW_MS;
}

/**
 * Returns the delay in milliseconds before firing for the given event type.
 */
export function getDelayMs(eventType: AgentEventType): number {
  return TRIGGER_DELAY_MAP[eventType] ?? 0;
}

/**
 * Returns true only for events that bypass suppression window.
 */
export function bypassesSuppression(eventType: AgentEventType): boolean {
  return eventType === "manual_help_requested";
}
